import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Input,
  Spinner,
  Badge,
} from "reactstrap";
import { useTranslation } from "context/TranslationProvider";
import dayjs from "dayjs";

const AdminCancellationModal = ({
  isOpen,
  toggle,
  listing,
  initialResolution = "approve",
  onConfirm,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [adminNotes, setAdminNotes] = useState("");
  const [selectedResolution, setSelectedResolution] = useState(initialResolution);

  useEffect(() => {
    if (isOpen) {
      setAdminNotes("");
      setSelectedResolution(initialResolution || "approve");
    }
  }, [isOpen, initialResolution]);

  if (!listing) return null;

  const currentStatus = (listing?.status || "").toLowerCase();
  const isCancellationRequested = currentStatus === "cancellation_requested";

  const requests = listing?.cancellationRequests || [];
  const activeReq = requests.slice().reverse().find(
    (r) =>
      r.status === "pending" ||
      r.status === "escalated" ||
      r.travelerResolution === "pending" ||
      r.senderResolution === "pending"
  );

  const cancellationReason = activeReq?.reason || listing?.cancellationReason || "";
  const cancellationExplanation = activeReq?.explanation || "";

  const fromPlace = listing?.fromPlace?.name || "-";
  const toPlace = listing?.toPlace?.name || "-";
  const listingType = listing?.type || (listing?.packageDetails ? "Sender" : "Traveler");

  const handleAction = (resolution) => {
    if (onConfirm) {
      onConfirm(resolution, adminNotes.trim());
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={isLoading ? undefined : toggle}
      centered
      backdrop={isLoading ? "static" : true}
      keyboard={!isLoading}
    >
      <ModalHeader toggle={isLoading ? undefined : toggle}>
        {isCancellationRequested
          ? t("admin.cancellation.adminResolutionTitle", "Admin Resolution Action")
          : t("admin.cancellation.cancelListingTitle", "Cancel Listing")}
      </ModalHeader>
      <ModalBody>
        {/* Listing Info Summary */}
        <div className="p-3 bg-secondary rounded mb-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="text-xs font-weight-bold text-uppercase text-muted">
              {listingType} Listing
            </span>
            <Badge color={isCancellationRequested ? "warning" : "danger"}>
              {isCancellationRequested
                ? t("page.userListings.status.cancellation_requested", "Cancellation Requested")
                : t("page.userListings.status.cancelled", "Cancelled")}
            </Badge>
          </div>
          <h5 className="mb-1 font-weight-bold">
            {fromPlace} → {toPlace}
          </h5>
          {listing?.userData && (
            <p className="text-xs text-muted mb-0">
              {t("common.owner", "Owner")}:{" "}
              <strong>
                {listing.userData.firstName || ""} {listing.userData.lastName || ""}
              </strong>{" "}
              {listing.userData.email ? `(${listing.userData.email})` : ""}
            </p>
          )}
        </div>

        {/* Cancellation Dispute Details if available */}
        {isCancellationRequested && (
          <div className="border rounded p-3 mb-3 bg-white">
            <h6 className="text-xs text-uppercase font-weight-bold text-warning mb-2">
              {t("admin.cancellation.activeRequestHeading", "Active Cancellation Request")}
            </h6>
            {cancellationReason && (
              <p className="text-xs text-dark mb-1">
                <strong>{t("table.headers.reason", "Reason")}:</strong> {cancellationReason}
              </p>
            )}
            {cancellationExplanation && (
              <p className="text-xs text-muted mb-1">
                <strong>{t("admin.cancellation.explanation", "Explanation")}:</strong>{" "}
                {cancellationExplanation}
              </p>
            )}
            {activeReq?.requestedAt && (
              <p className="text-xs text-muted mb-0">
                <strong>{t("admin.cancellation.requestedAt", "Requested At")}:</strong>{" "}
                {dayjs(activeReq.requestedAt).format("DD MMM YYYY, hh:mm A")}
              </p>
            )}
          </div>
        )}

        {/* Help text */}
        <p className="text-xs text-muted mb-3">
          {isCancellationRequested
            ? t(
                "admin.cancellation.adminResolutionDesc",
                "Review the dispute details above. Approving will finalize cancellation of the listing and associated order. Rejecting will keep the delivery order active."
              )
            : t(
                "admin.cancellation.directCancelDesc",
                "You are cancelling this listing as administrator. Please provide an admin decision note."
              )}
        </p>

        {/* Admin Notes Input */}
        <FormGroup className="mb-1">
          <label className="form-control-label text-xs font-weight-bold">
            {t("admin.cancellation.notesLabel", "Admin Decision Notes (Optional)")}
          </label>
          <Input
            type="textarea"
            rows="3"
            placeholder={t("admin.cancellation.notesPlaceholder", "Reason for admin resolution...")}
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            disabled={isLoading}
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between align-items-center">
        <Button color="secondary" outline onClick={toggle} disabled={isLoading} size="sm">
          {t("create-listing.buttons.cancel", "Cancel")}
        </Button>

        <div className="d-flex align-items-center">
          {isCancellationRequested ? (
            <>
              <Button
                color="secondary"
                size="sm"
                className="mr-2 font-weight-bold"
                onClick={() => handleAction("reject")}
                disabled={isLoading}
              >
                {isLoading && selectedResolution === "reject" && (
                  <Spinner size="sm" className="mr-1" />
                )}
                {t("admin.cancellation.rejectBtn", "Reject Cancellation (Resume Order)")}
              </Button>
              <Button
                color="danger"
                size="sm"
                className="font-weight-bold"
                onClick={() => handleAction("approve")}
                disabled={isLoading}
              >
                {isLoading && selectedResolution === "approve" && (
                  <Spinner size="sm" className="mr-1" />
                )}
                {t("admin.cancellation.approveBtn", "Approve Cancellation")}
              </Button>
            </>
          ) : (
            <Button
              color="danger"
              size="sm"
              className="font-weight-bold"
              onClick={() => handleAction("approve")}
              disabled={isLoading}
            >
              {isLoading && <Spinner size="sm" className="mr-1" />}
              {t("admin.cancellation.cancelListingBtn", "Cancel Listing")}
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default AdminCancellationModal;

